import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import type {
	MarginMode,
	OrderSide,
	OrderType,
	TimeInForce,
} from "../constants/order-form.constants.ts";
import {
	createOrderDraft,
	type OrderDraft,
} from "../hooks/order-form-model.ts";

export type AmountInputMode = "amount" | "quoteAmount";

// ── Persisted settings (localStorage via atomWithStorage) ───────────

export const orderTypeAtom = atomWithStorage<OrderType>(
	"trade_order_type",
	"Limit order",
);

export const autoQuoteAtom = atomWithStorage<boolean>(
	"order_auto_quote_key",
	false,
);

export const amountInputModeAtom = atomWithStorage<AmountInputMode>(
	"trade_order_amount_input_mode",
	"quoteAmount",
);

export const timeInForceAtom = atomWithStorage<TimeInForce>(
	"trade_time_in_force",
	"GTC",
);

// ── Ephemeral form state (reset on coin change) ────────────────────

export const orderSideAtom = atom<OrderSide>("buy");

export const orderDraftAtom = atom<OrderDraft>(
	createOrderDraft("quoteAmount", false),
);

export const triggerPriceAtom = atom("");
export const twapDurationAtom = atom("30");
export const twapNumSlicesAtom = atom("10");
export const reduceOnlyAtom = atom(false);

// ── Leverage & margin mode (per coin, synced from localStorage) ────

export const marginModeAtom = atom<MarginMode>("Cross");
export const leverageIndexAtom = atom(2);

/** Flag: true once we've applied the API's current leverage for this coin */
export const hasInitFromApiAtom = atom(false);

// ── Stored leverage utilities (per address:coin in localStorage) ───

type StoredLeverageEntry = { leverage: number; marginMode: MarginMode };
type StoredLeverageMap = Record<string, StoredLeverageEntry>;

const LEVERAGE_STORAGE_KEY = "trade_leverage_settings_v1";

function readStoredLeverageMap(): StoredLeverageMap {
	try {
		const raw = localStorage.getItem(LEVERAGE_STORAGE_KEY);
		return raw ? JSON.parse(raw) : {};
	} catch {
		return {};
	}
}

export function getStoredLeverage(
	address: string | null | undefined,
	coin: string,
): StoredLeverageEntry | null {
	if (!address) return null;
	return readStoredLeverageMap()[`${address}:${coin}`] ?? null;
}

export function setStoredLeverage(
	address: string | null | undefined,
	coin: string,
	entry: StoredLeverageEntry,
) {
	if (!address) return;
	const map = readStoredLeverageMap();
	map[`${address}:${coin}`] = entry;
	localStorage.setItem(LEVERAGE_STORAGE_KEY, JSON.stringify(map));
}
